import type { NonAidRepackageDto } from 'api/generated/schemas';

import { EMPTY_VALUE } from '../results-step.constants';

import type { ChangeValue, MetricKind } from '../results-step.types';

const hasValue = (value: number | null | undefined): value is number =>
  value !== null && value !== undefined && Number.isFinite(value);

export const formatCount = (value: number | null | undefined): string =>
  hasValue(value) ? Math.round(value).toLocaleString('en-US') : EMPTY_VALUE;

export const formatCurrency = (value: number | null | undefined): string =>
  hasValue(value)
    ? `$${Math.round(value).toLocaleString('en-US')}`
    : EMPTY_VALUE;

export const formatRate = (fraction: number | null | undefined): string =>
  hasValue(fraction) ? `${(fraction * 100).toFixed(1)}%` : EMPTY_VALUE;

export const formatMetricValue = (
  value: number | null | undefined,
  kind: MetricKind
): string => {
  switch (kind) {
    case 'currency':
      return formatCurrency(value);
    case 'rate':
      return formatRate(value);
    case 'count':
    default:
      return formatCount(value);
  }
};

const resolveDirection = (value: number): ChangeValue['direction'] => {
  if (value > 0) return 'up';
  if (value < 0) return 'down';

  return 'neutral';
};

const withSign = (value: number, body: string): string =>
  `${value > 0 ? '+' : value < 0 ? '-' : ''}${body}`;

/** Relative percent change between two numeric values, e.g. `+4.42%`. */
export const buildPercentChange = (
  current: number | null | undefined,
  base: number | null | undefined
): ChangeValue | null => {
  if (!hasValue(current) || !hasValue(base) || base === 0) return null;

  const ratio = ((current - base) / base) * 100;
  const rounded = Math.round(ratio * 100) / 100;

  return {
    value: rounded,
    label: withSign(rounded, `${Math.abs(rounded)}%`),
    direction: resolveDirection(rounded),
  };
};

/** Difference in percentage points for rate metrics, e.g. `-1%`. */
export const buildPointChange = (
  current: number | null | undefined,
  base: number | null | undefined
): ChangeValue | null => {
  if (!hasValue(current) || !hasValue(base)) return null;

  const points = (current - base) * 100;
  const rounded = Math.round(points * 100) / 100;

  return {
    value: rounded,
    label: withSign(rounded, `${Math.abs(rounded)}%`),
    direction: resolveDirection(rounded),
  };
};

const PREVIOUS_AWARDS_REASON_LABELS: Array<[keyof NonAidRepackageDto, string]> =
  [
    ['notIncludedStudents', 'kept previous award'],
    ['studentsOutOfRegime', 'no regime configured for their population'],
    ['studentsOutOfCell', 'no matching cost-of-attendance cell'],
    ['studentsUnableToRepackage', 'missing academic rank data'],
  ];

/** Reasons a group's students kept their previous award instead of a repackaged one. */
export const buildPreviousAwardsReasonsText = (
  status: NonAidRepackageDto | undefined
): string => {
  if (!status) return '';

  const reasons = PREVIOUS_AWARDS_REASON_LABELS.filter(
    ([field]) => Number(status[field]) > 0
  ).map(([, label]) => label);

  return reasons.length ? `Reasons: ${reasons.join(', ')}` : '';
};

/** Absolute volume delta for count/currency metrics, e.g. `+220,054`. */
export const buildVolumeChange = (
  current: number | null | undefined,
  base: number | null | undefined,
  kind: MetricKind
): ChangeValue | null => {
  if (!hasValue(current) || !hasValue(base)) return null;

  const delta = Math.round(current - base);
  const magnitude = Math.abs(delta).toLocaleString('en-US');
  const body = kind === 'currency' ? `$${magnitude}` : magnitude;

  return {
    value: delta,
    label: withSign(delta, body),
    direction: resolveDirection(delta),
  };
};
